import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { useUserStore } from "./UserStore";
import { Bookmark, BookmarkType } from "@/types/api/Bookmark";

export const useFavoriteStore = defineStore("favorite", () => {
  const selectedBookmark = ref<Bookmark>();
  const bookmarkError = ref<{ error: string } | null>(null);
  const favorites = ref<Record<BookmarkType, Bookmark[]>>({
    [BookmarkType.AudienceBuilder]: [],
    [BookmarkType.TvRating]: [],
    [BookmarkType.HispanicRating]: [],
    [BookmarkType.ReachAndFrequencyV2]: [],
    [BookmarkType.PoliticalPaidMedia]: [],
  });
  const savingBookmark = ref(false);
  const loadingBookmark = ref(false);
  const saveBookmarkFlag = ref(false);
  const canEditBookmark = ref(true);

  const setUserBookmarks = (payload: {
    type: BookmarkType;
    data: Bookmark[];
  }) => {
    const userStore = useUserStore();
    const currentOrg = userStore.currentOrg?.id;
    const currentAppClient = userStore.currentAppClient?.id;
    const user = userStore.currentUser;

    const bookmarksInContext = payload.data.filter((p) => {
      if (currentAppClient) {
        return (
          !p.org_id &&
          (!p.app_client_id || p.app_client_id === currentAppClient)
        );
      }
      if (currentOrg) {
        return !p.app_client_id && (!p.org_id || p.org_id === currentOrg);
      }
      return true;
    });

    // if shared a bookmark from an org you dont have - add to default org view
    if (user?.default_org_id === currentOrg) {
      payload.data.forEach((p) => {
        const hasOrg = user!.orgs.find((org) => org.id === p.org_id);
        if (!hasOrg && p.org_id && !p.app_client_id) {
          bookmarksInContext.push(p);
        }
      });
    }

    // if shared a bookmark from an app client you dont have - add to default app client view
    if (user?.default_app_client_id === currentAppClient) {
      payload.data.forEach((p) => {
        const hasAppClient = user!.app_clients?.find(
          (client) => client.id === p.app_client_id,
        );
        if (!hasAppClient && p.app_client_id && !p.org_id) {
          bookmarksInContext.push(p);
        }
      });
    }

    favorites.value[payload.type] = bookmarksInContext.sort(
      (a, b) => b.id - a.id,
    );
  };

  const removeUserBookmark = (payload: Bookmark) => {
    const idx = favorites.value[payload.type].findIndex(
      (b) => b.id === payload.id,
    );
    if (idx >= 0) {
      favorites.value[payload.type].splice(idx, 1);
    }
  };

  const updateUserBookmark = (payload: Bookmark) => {
    const idx = favorites.value[payload.type].findIndex(
      (b) => b.id === payload.id,
    );
    if (idx >= 0) {
      favorites.value[payload.type].splice(idx, 1, payload);
    }
    if (selectedBookmark.value?.id === payload.id) {
      selectedBookmark.value = payload;
    }
  };

  return {
    favorites,
    selectedBookmark,
    savingBookmark,
    loadingBookmark,
    saveBookmarkFlag,
    canEditBookmark,

    setUserBookmarks,
    removeUserBookmark,
    updateUserBookmark,

    getBookmarkById: async (bookmark_id: number) => {
      loadingBookmark.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/bookmarks/{bookmark_id}",
          {
            params: { path: { bookmark_id } },
          },
        );
        if (error) {
          throw error;
        }
        selectedBookmark.value = data as Bookmark;
      } catch (err: any) {
        selectedBookmark.value = undefined;
        bookmarkError.value = { error: err };
      } finally {
        loadingBookmark.value = false;
      }
    },
    getBookmarksForUser: async (params: { type: BookmarkType }) => {
      loadingBookmark.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/users/bookmarks", {
          params: { query: params as never },
        });
        if (error) {
          throw error;
        }
        setUserBookmarks({
          type: params.type,
          data: (data as unknown as Bookmark[]) ?? [],
        });
      } finally {
        loadingBookmark.value = false;
      }
    },
    createBookmark: async (params: Bookmark) => {
      savingBookmark.value = true;
      try {
        const { data, error } = await client.POST("/api/v1/bookmarks", {
          body: params as never,
        });
        if (error) {
          throw error;
        }
        const bookmark = data as Bookmark;
        updateUserBookmark(bookmark);
        return bookmark;
      } finally {
        savingBookmark.value = false;
      }
    },
    updateBookmark: async (params: Bookmark) => {
      savingBookmark.value = true;
      try {
        const { data, error } = await client.PUT(
          "/api/v1/bookmarks/{bookmark_id}",
          {
            params: { path: { bookmark_id: params.id } },
            body: {
              meta_data: params.meta_data,
              shared_with: params.shared_with,
            } as never,
          },
        );
        if (error) {
          throw error;
        }
        updateUserBookmark(data as Bookmark);
      } finally {
        savingBookmark.value = false;
      }
    },
    deleteBookmark: async (params: Bookmark) => {
      savingBookmark.value = true;
      try {
        const { error } = await client.DELETE(
          "/api/v1/bookmarks/{bookmark_id}",
          {
            params: { path: { bookmark_id: params.id } },
          },
        );
        if (error) {
          throw error;
        }
        removeUserBookmark(params);
      } finally {
        savingBookmark.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFavoriteStore, import.meta.hot));
}
